'use client';
import React, { useEffect } from 'react';
import { chartData } from './charts.config';
import NotFound from '../../app/[...404]/page';
import FactionTopPlayers from './FactionTopPlayers';
import FactionDetails from './FactionDetails';
import FactionDiagram from './FactionDiagram';
import FactionMobileStats from './FactionMobileStats';

import type {
  FactionProfile,
  FactionTrends,
} from '@wwe-portal/entities/faction.type';
import { DateTime } from 'luxon';
import FactionCharts from './FactionCharts';
import FactionCollectionStats from './FactionCollectionStats';

import { cn } from '@wwe-portal/ui/lib/lib';
import TopEventsHeaderItem from './TopEventsHeaderItem/TopEventsHeaderItem';
import { useTranslations } from 'next-intl';
import { HistoryEventLeaderboard } from '../leaderboard/history-event-leaderboard';

function FactionProfile({
  faction,
  factionName,
  factionTrends,
  factionLeaderboards,
}: Readonly<{
  factionName: string;
  faction: FactionProfile;
  factionTrends: FactionTrends[];
  factionLeaderboards: any[];
}>) {
  const t = useTranslations();
  useEffect(() => {
    if (factionName) {
      //   Promise.all([
      //     dispatch(
      //       leaderboardOp.getFactionProfileLeaderboardByFactionNameAsync(
      //         factionName
      //       )
      //     ),
      //   ]);
    }
  }, [factionName]);

  /**
   * Snap Shot Month month array
   */
  const snapShotMonth = factionTrends.map((item) =>
    DateTime.fromISO(item.snapshot_month).toFormat('LLL')
  );

  /**
   * Feud Battles array
   */
  const feudBattles = factionTrends
    ? factionTrends.map((item) => item.feud_battles)
    : [];
  /**
   * Faction Help Sent array
   */
  const factionHelpSent = factionTrends
    ? factionTrends.map((item) => item.faction_help_sent)
    : [];
  /**
   * Faction Missions array
   */
  const factionMissions = factionTrends
    ? factionTrends.map((item) => item.faction_missions)
    : [];
  /**
   * Faction Token array
   */
  const factionTokenSpent = factionTrends.map(
    (item) => item.faction_token_spent
  );
  /**
   * Total Battle Wins
   */
  const totalWins = factionTrends
    .map((item) => item.battle_wins)
    .filter((item) => item !== 0);
  /**
   * Total Battle loses
   */
  const totalLosses = factionTrends
    .map((item: { battle_losses: any }) =>
      item.battle_losses !== 0 ? -item.battle_losses : item.battle_losses
    )
    .filter((item) => -item !== 0);

  const chartDataList = chartData({
    feudBattles,
    factionHelpSent,
    factionMissions,
    factionTokenSpent,
  });

  // sort by endDate if factionRank is the same
  const playerProfileTopEvents = factionLeaderboards
    .sort((a, b) => {
      if (a.factionRank === b.factionRank && a.endDate && b.endDate) {
        return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
      }
      return a.factionRank - b.factionRank;
    })
    .slice(0, 5);

  const factionProfiles = factionLeaderboards;

  return (
    <div
      className={cn('py-5 pt-[30px] sm:px-0 md:py-7', {
        'h-full w-full p-0': !faction,
      })}
    >
      {faction ? <FactionDetails faction={faction} /> : <NotFound />}

      {factionTrends?.length && faction ? (
        <>
          <FactionMobileStats
            isDiagram={true}
            totalWins={totalWins}
            totalLosses={totalLosses}
            chart={undefined}
          />
          <FactionDiagram
            totalWins={totalWins}
            totalLosses={totalLosses}
            snapShotMonth={snapShotMonth}
          />
        </>
      ) : null}

      <FactionTopPlayers playersType="active" faction={faction} />
      <FactionTopPlayers playersType="strongest" faction={faction} />

      <FactionCollectionStats faction={faction} />

      <div className="mb-5">
        <div className="grid grid-cols-1 gap-0 md:gap-8 2xl:grid-cols-2">
          {factionTrends?.length && faction
            ? chartDataList.map((item) => (
                <div key={item.id} className="rounded-md">
                  <FactionMobileStats
                    isDiagram={false}
                    chart={item}
                    totalWins={[]}
                    totalLosses={[]}
                  />
                  <FactionCharts chart={item} xLabels={snapShotMonth} />
                </div>
              ))
            : null}
        </div>
      </div>

      <div className="my-[30px] text-xl font-medium">
        {t('top_faction_feud_finishes')}{' '}
        <span className="text-text500 text-sm">({t('last_365_days')})</span>
      </div>

      {playerProfileTopEvents?.length ? (
        <div className="bg-back400 mb-5 mt-7 rounded-[10px] p-[30px] px-5 pb-[30px] pt-0">
          <div className="faction-profile-collection relative mt-7 grid grid-cols-1 items-center justify-between rounded-[10px] py-[30px] md:grid-cols-6 xl:grid-cols-5">
            {playerProfileTopEvents.map((topEvent: any, index) => {
              return (
                <div
                  key={topEvent.eventId}
                  className={cn(
                    'faction-profile-collection-stats col-span-1 xl:col-span-1',
                    {
                      'md:col-span-3': index > 2,
                      'md:col-span-2': index <= 2,
                    }
                  )}
                >
                  <TopEventsHeaderItem
                    isLast={index === (playerProfileTopEvents || []).length - 1}
                    isPrevious={
                      index === (playerProfileTopEvents || []).length - 2
                    }
                    isThird={
                      index === (playerProfileTopEvents || []).length - 3
                    }
                    key={topEvent.eventId}
                    name={topEvent.name}
                    endDate={topEvent.endDate}
                    rank={topEvent.factionRank}
                    eventId={topEvent.eventId}
                  />
                </div>
              );
            })}
            <hr className="faction-profile-collection-hr hidden md:block xl:hidden" />
          </div>
        </div>
      ) : null}

      <HistoryEventLeaderboard
        data={factionProfiles.map((player) => ({
          points: player.factionPoints,
          rank: player.factionRank,
          player: player.name ?? '-',
          event: player.additionalName
            ? `${player.name}-${player.additionalName}`
            : player.name || '-',
          endDate: player.endDate
            ? DateTime.fromISO(player.endDate).toFormat('MM/dd/yyyy')
            : '-',
          name: player.name ?? '-',
          userId: player.userId ?? '-',
        }))}
        desktopHeader={undefined}
        mobileHeader={
          <div className="pl-5 pt-5">
            <div className="grid grid-cols-12 gap-5 px-5">
              <div className="text-secondary col-span-3 text-sm font-semibold uppercase">
                Rank
              </div>
              <div className="text-secondary col-span-6 text-sm font-semibold uppercase">
                Event
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
}

export default FactionProfile;
